import React, { useState, useEffect, useCallback } from 'react';
import {
  CompanyArtifactId,
  CompanyStateId,
  CArtifactStateItem,
} from '../../types/artifacts-branded';
import styles from './StateManagement.module.css';

interface StateManagementProps {
  artifactId: CompanyArtifactId;
}

export const StateManagement: React.FC<StateManagementProps> = ({ artifactId }) => {
  const [states, setStates] = useState<CArtifactStateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingState, setEditingState] = useState<CArtifactStateItem | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    state_type: 'active' as 'creation' | 'active' | 'consumption',
    is_initial: false,
    is_final: false,
    is_required: false,
    is_improvement: false,
  });

  const loadStates = useCallback(async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await artifactStateService.getStates(artifactId);
      // setStates(response);

      // Mock data for now
      setStates([
        {
          id: '1' as CompanyStateId,
          artifact_id: artifactId,
          name: 'Created',
          description: 'Initial state when artifact is created',
          is_initial: true,
          is_final: false,
          is_improvement: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: '2' as CompanyStateId,
          artifact_id: artifactId,
          name: 'Active',
          description: 'Artifact is active and being used',
          is_initial: false,
          is_final: false,
          is_improvement: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: '3' as CompanyStateId,
          artifact_id: artifactId,
          name: 'Completed',
          description: 'Artifact has been completed',
          is_initial: false,
          is_final: true,
          is_improvement: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load states');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    // TODO: Load states from API
    void loadStates();
  }, [loadStates]);

  const handleAddState = () => {
    setShowAddForm(true);
    setEditingState(null);
    setFormData({
      name: '',
      description: '',
      state_type: 'active',
      is_initial: false,
      is_final: false,
      is_required: false,
      is_improvement: false,
    });
  };

  const handleEditState = (state: CArtifactStateItem) => {
    setEditingState(state);
    setShowAddForm(true);
    setFormData({
      name: state.name,
      description: state.description || '',
      state_type: 'active', // Default since CArtifactStateItem doesn't have state_type
      is_initial: state.is_initial,
      is_final: state.is_final,
      is_required: false, // Default since CArtifactStateItem doesn't have is_required
      is_improvement: false, // Default since CArtifactStateItem doesn't have is_improvement
    });
  };

  const handleDeleteState = async (stateId: string) => {
    if (window.confirm('Are you sure you want to delete this state?')) {
      try {
        // TODO: Replace with actual API call
        // await artifactStateService.deleteState(artifactId, stateId);
        setStates(states.filter((state) => state.id !== stateId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete state');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingState) {
        // TODO: Replace with actual API call
        // await artifactStateService.updateState(artifactId, editingState.id, formData);
        setStates(
          states.map((state) =>
            state.id === editingState.id
              ? { ...state, ...formData, updated_at: new Date().toISOString() }
              : state
          )
        );
      } else {
        // TODO: Replace with actual API call
        // const newState = await artifactStateService.createState(artifactId, formData);
        const newState: CArtifactStateItem = {
          id: Date.now().toString() as CompanyStateId,
          artifact_id: artifactId,
          name: formData.name,
          description: formData.description,
          is_initial: formData.is_initial,
          is_final: formData.is_final,
          is_improvement: formData.is_improvement,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setStates([...states, newState]);
      }

      setShowAddForm(false);
      setEditingState(null);
      setFormData({
        name: '',
        description: '',
        state_type: 'active',
        is_initial: false,
        is_final: false,
        is_required: false,
        is_improvement: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save state');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingState(null);
    setFormData({
      name: '',
      description: '',
      state_type: 'active',
      is_initial: false,
      is_final: false,
      is_required: false,
      is_improvement: false,
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (loading) {
    return <div className={styles.stateManagementLoading}>Loading states...</div>;
  }

  if (error) {
    return <div className={styles.stateManagementError}>Error: {error}</div>;
  }

  return (
    <div className={styles.stateManagement}>
      <div className={styles.stateHeader}>
        <h3>States ({states.length})</h3>
        <button className={styles.addStateButton} onClick={handleAddState}>
          Add State
        </button>
      </div>

      {showAddForm && (
        <div className={styles.stateFormOverlay}>
          <div className={`${styles.stateManagement} ${styles.stateForm}`}>
            <h4>{editingState ? 'Edit State' : 'Add New State'}</h4>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="state_type">State Type *</label>
                <select
                  id="state_type"
                  name="state_type"
                  value={formData.state_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="creation">Creation</option>
                  <option value="active">Active</option>
                  <option value="consumption">Consumption</option>
                </select>
              </div>

              <div className={`form-group ${styles.checkboxGroup}`}>
                <label>
                  <input
                    type="checkbox"
                    name="is_initial"
                    checked={formData.is_initial}
                    onChange={handleInputChange}
                  />
                  Is Initial State
                </label>
              </div>

              <div className={`form-group ${styles.checkboxGroup}`}>
                <label>
                  <input
                    type="checkbox"
                    name="is_final"
                    checked={formData.is_final}
                    onChange={handleInputChange}
                  />
                  Is Final State
                </label>
              </div>

              <div className={`form-group ${styles.checkboxGroup}`}>
                <label>
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={handleInputChange}
                  />
                  Is Required
                </label>
              </div>

              <div className={`form-group ${styles.checkboxGroup}`}>
                <label>
                  <input
                    type="checkbox"
                    name="is_improvement"
                    checked={formData.is_improvement}
                    onChange={handleInputChange}
                  />
                  Is Improvement
                </label>
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton}>
                  {editingState ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={handleCancel} className={styles.cancelButton}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.statesList}>
        {states.length === 0 ? (
          <div className={styles.noStates}>
            <p>No states defined for this artifact.</p>
            <p>Click "Add State" to create the first state.</p>
          </div>
        ) : (
          <div className={styles.statesGrid}>
            {states.map((state) => (
              <div key={state.id} className={styles.stateCard}>
                <div className={styles.stateHeader}>
                  <h4>{state.name}</h4>
                  <div className={styles.stateActions}>
                    <button onClick={() => handleEditState(state)} className={styles.editButton}>
                      Edit
                    </button>
                    <button onClick={() => handleDeleteState(state.id)} className={styles.deleteButton}>
                      Delete
                    </button>
                  </div>
                </div>

                <div className={styles.stateContent}>
                  <p className={styles.stateDescription}>{state.description || 'No description'}</p>

                  <div className={styles.stateMeta}>
                    <span className={styles.stateType}>State</span>
                    {state.is_initial && <span className={`${styles.stateFlag} ${styles.initial}`}>Initial</span>}
                    {state.is_final && <span className={`${styles.stateFlag} ${styles.final}`}>Final</span>}
                    {state.is_improvement && <span className={`${styles.stateFlag} ${styles.improvement}`}>Improvement</span>}
                  </div>

                  <div className={styles.stateDates}>
                    <small>Created: {new Date(state.created_at).toLocaleDateString()}</small>
                    <small>Updated: {new Date(state.updated_at).toLocaleDateString()}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
